import { useState } from "react";
import {
  X, Search, MapPin, Star, Phone, Globe, CheckCircle2, Loader2, Zap,
  ExternalLink, Award, Building2, DollarSign, Calendar,
} from "lucide-react";
import api from "../lib/api";
import PlaceInput from "./PlaceInput";

const KEYWORD_OPTIONS = [
  { value: "lodge", label: "Lodge" },
  { value: "game lodge", label: "Game lodge" },
  { value: "hotel", label: "Hotel" },
  { value: "guest house", label: "Guest house" },
  { value: "bnb", label: "B&B" },
  { value: "resort", label: "Resort" },
  { value: "restaurant", label: "Restaurant" },
  { value: "spa", label: "Spa" },
];

const PRICE_LABEL = {
  PRICE_LEVEL_FREE: "Free",
  PRICE_LEVEL_INEXPENSIVE: "$",
  PRICE_LEVEL_MODERATE: "$$",
  PRICE_LEVEL_EXPENSIVE: "$$$",
  PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
};

const SORT_OPTIONS = [
  { value: "hot", label: "Hot prospect score" },
  { value: "stars", label: "Official ★ rating" },
  { value: "google", label: "Google rating" },
  { value: "price", label: "Price (high → low)" },
  { value: "az", label: "A → Z" },
];

function fmtZAR(n) {
  if (!n) return "";
  return `R${Number(n).toLocaleString("en-ZA")}`;
}

function HotScoreBadge({ score }) {
  if (score == null) return null;
  let color = "#647087";
  let label = "Cold";
  if (score >= 70) { color = "#10B981"; label = "🔥 Hot"; }
  else if (score >= 45) { color = "#F59E0B"; label = "Warm"; }
  else if (score >= 20) { color = "#1FB6C1"; label = "Lukewarm"; }
  return (
    <span
      className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border flex items-center gap-1"
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}15` }}
      title={`Hot prospect score: ${score}/100`}
    >
      <span className="font-bold">{score}</span>{label}
    </span>
  );
}

function StarBar({ rating, official, count }) {
  const r = Math.round(official || rating || 0);
  if (!r) return null;
  return (
    <span className="flex items-center gap-0.5 text-[11px]">
      {[1,2,3,4,5].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i <= r ? "text-amber-300 fill-amber-300" : "text-white/15"}`} />
      ))}
      <span className="text-[#a0aabf] ml-0.5 font-mono">
        {official ? `${official} TGCSA` : `${rating}${count != null ? ` (${count})` : ""}`}
      </span>
    </span>
  );
}

export default function DiscoverProspectsDialog({ onClose, onCommitted }) {
  const [location, setLocation] = useState(null);
  const [radiusKm, setRadiusKm] = useState(25);
  const [keyword, setKeyword] = useState("lodge");
  const [maxResults, setMaxResults] = useState(15);
  const [quickScan, setQuickScan] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [enrichProgress, setEnrichProgress] = useState({ done: 0, total: 0 });
  const [selected, setSelected] = useState(new Set());
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(0);
  const [err, setErr] = useState("");
  const [sort, setSort] = useState("hot");

  const enrichOne = async (idx, prospect) => {
    try {
      const { data } = await api.post("/research/enrich-prospect", {
        place_id: prospect.place_id,
        name: prospect.name,
        website: prospect.website,
      });
      setResults((prev) => prev.map((r, i) => i === idx ? { ...r, ...data, _enriched: true } : r));
    } catch (_e) {
      setResults((prev) => prev.map((r, i) => i === idx ? { ...r, _enriched: true } : r));
    }
  };

  const search = async () => {
    if (!location?.address?.trim()) return;
    setLoading(true);
    setErr("");
    setResults([]);
    setSelected(new Set());
    setEnrichProgress({ done: 0, total: 0 });
    try {
      const body = { radius_km: Number(radiusKm), keyword, max_results: Number(maxResults) };
      if (location.lat != null && location.lng != null) {
        body.lat = location.lat; body.lng = location.lng;
        body.location_query = location.address;
      } else {
        body.location_query = location.address.trim();
      }
      const { data } = await api.post("/research/discover-prospects", body);
      const prospects = data.prospects || [];
      setResults(prospects);
      if (prospects.length === 0) {
        setErr("No new prospects found (or all already in SalesFlow).");
      } else if (quickScan) {
        // Fan out enrichment, max 4 in parallel
        setEnrichProgress({ done: 0, total: prospects.length });
        const queue = prospects.map((p, i) => [i, p]);
        const concurrency = 4;
        const workers = Array.from({ length: concurrency }, async () => {
          while (queue.length) {
            const next = queue.shift();
            if (!next) return;
            await enrichOne(next[0], next[1]);
            setEnrichProgress((prev) => ({ ...prev, done: prev.done + 1 }));
          }
        });
        await Promise.all(workers);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Search failed");
    }
    setLoading(false);
  };

  const toggleAll = () => {
    if (selected.size === results.length) setSelected(new Set());
    else setSelected(new Set(results.map((r) => r.place_id)));
  };
  const toggle = (pid) => {
    const n = new Set(selected);
    if (n.has(pid)) n.delete(pid); else n.add(pid);
    setSelected(n);
  };

  const commit = async () => {
    if (selected.size === 0) return;
    setCommitting(true);
    setErr("");
    let created = 0;
    try {
      const toCommit = results.filter((r) => selected.has(r.place_id));
      for (const r of toCommit) {
        try {
          await api.post("/research/commit-prospect", {
            place_id: r.place_id,
            name: r.name,
            phone: r.phone,
            website: r.website,
            address: r.address,
            lat: r.lat,
            lng: r.lng,
            rating: r.rating,
            price_level: r.price_level,
            industry: keyword.includes("lodge") || keyword === "resort" ? "lodge"
              : keyword === "restaurant" ? "restaurant" : "other",
            // Enriched fields (only present after Quick scan / Enrich now)
            official_star_rating: r.official_star_rating,
            price_min: r.price_min,
            price_max: r.price_max,
            rooms_count: r.rooms_count,
            established_year: r.established_year,
            hot_score: r.hot_score,
            tgcsa_url: r.tgcsa_url,
          });
          created++;
        } catch (e) {
          // Single-row failure during bulk commit shouldn't break the
          // whole batch — but iter 290j surfaces the reason via the
          // console so users / support can debug a short final count.
          console.warn("DiscoverProspects bulk-commit skipped a row:", e);
        }
      }
      setCommitted(created);
      if (onCommitted) onCommitted(created);
      setResults((prev) => prev.filter((r) => !selected.has(r.place_id)));
      setSelected(new Set());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Commit failed");
    }
    setCommitting(false);
  };

  // Sorting
  const sortedResults = [...results].sort((a, b) => {
    if (sort === "hot") return (b.hot_score || 0) - (a.hot_score || 0);
    if (sort === "stars") return (b.official_star_rating || 0) - (a.official_star_rating || 0);
    if (sort === "google") return (b.rating || 0) - (a.rating || 0);
    if (sort === "price") return (b.price_max || 0) - (a.price_max || 0);
    if (sort === "az") return (a.name || "").localeCompare(b.name || "");
    return 0;
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="discover-dialog"
    >
      <div onClick={(e) => e.stopPropagation()} className="hv-card max-w-6xl w-full max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <Search className="w-5 h-5 text-[#1fb6c1]" />
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold">Discover prospects</h2>
            <p className="text-[11px] text-[#647087]">
              Find new prospects via Google Places, then deep-enrich with official TGCSA stars + website pricing + Wikipedia history. Existing leads/customers auto-deduped.
            </p>
          </div>
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="discover-close">
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        {/* Filter row */}
        <div className="p-4 border-b border-white/5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">Location</label>
            <PlaceInput
              value={location}
              onChange={setLocation}
              placeholder="e.g. Hazyview or Kruger National Park"
              testId="discover-location"
              compact
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">Radius (km)</label>
            <input type="number" value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)} min={1} max={100} className="hv-input text-sm w-full" data-testid="discover-radius" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">Type</label>
            <select value={keyword} onChange={(e) => setKeyword(e.target.value)} className="hv-input text-sm w-full" data-testid="discover-type">
              {KEYWORD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">Max results</label>
            <input type="number" value={maxResults} onChange={(e) => setMaxResults(e.target.value)} min={1} max={20} className="hv-input text-sm w-full" data-testid="discover-max" />
          </div>
          <div className="sm:col-span-2 lg:col-span-5 flex justify-between items-center flex-wrap gap-2">
            <label className="flex items-center gap-2 text-xs text-[#a0aabf] cursor-pointer">
              <input
                type="checkbox"
                checked={quickScan}
                onChange={(e) => setQuickScan(e.target.checked)}
                className="accent-[#1fb6c1]"
                data-testid="discover-quickscan"
              />
              <Zap className="w-3 h-3 text-amber-300" />
              <span>Quick scan: pull TGCSA stars, pricing, and history for every result (~3s/place)</span>
            </label>
            <button type="button" onClick={search} disabled={loading || !location?.address?.trim()} className="hv-btn hv-btn-primary text-sm" data-testid="discover-search">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? "Searching…" : "Search"}
            </button>
          </div>
          {enrichProgress.total > 0 && (
            <div className="sm:col-span-2 lg:col-span-5">
              <div className="text-[10px] text-[#a0aabf] flex items-center gap-2">
                <Zap className="w-3 h-3 text-amber-300" />
                Enriching {enrichProgress.done}/{enrichProgress.total}…
                <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-300 transition-all duration-300"
                    style={{ width: `${(enrichProgress.done / enrichProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {err && <div className="p-3 text-xs text-rose-300 bg-rose-500/10 border-b border-rose-500/30">{err}</div>}
        {committed > 0 && (
          <div className="p-3 text-xs text-emerald-300 bg-emerald-500/10 border-b border-emerald-500/30" data-testid="discover-committed-banner">
            ✓ Added {committed} new lead{committed === 1 ? "" : "s"} to SalesFlow.
          </div>
        )}

        {/* Toolbar above results */}
        {results.length > 0 && (
          <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0a0e17] z-10 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={selected.size === results.length} onChange={toggleAll} className="accent-[#1fb6c1]" data-testid="discover-select-all" />
              <span className="text-[#a0aabf]">
                <span className="text-white font-mono">{selected.size}</span> / {results.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="hv-input text-xs py-1" data-testid="discover-sort">
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
              </select>
              <button type="button" onClick={commit} disabled={selected.size === 0 || committing} className="hv-btn hv-btn-primary text-xs" data-testid="discover-commit">
                {committing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {committing ? "Adding…" : `Add ${selected.size} to leads`}
              </button>
            </div>
          </div>
        )}

        {/* Rich-card results */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {sortedResults.map((r, idx) => {
            const realIdx = results.findIndex((x) => x.place_id === r.place_id);
            const isSelected = selected.has(r.place_id);
            const priceRange = r.price_max
              ? `${r.price_min ? fmtZAR(r.price_min) : ""}${r.price_min && r.price_max && r.price_min !== r.price_max ? " — " : r.price_min ? "" : "up to "}${fmtZAR(r.price_max)}/n`
              : null;
            return (
              <div
                key={r.place_id}
                className={`hv-card p-3 transition cursor-pointer ${isSelected ? "ring-1 ring-[#1fb6c1] bg-[#1fb6c1]/5" : "hover:bg-white/[0.02]"}`}
                onClick={() => toggle(r.place_id)}
                data-testid={`discover-row-${r.place_id}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(r.place_id)}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-[#1fb6c1] mt-1 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    {/* Title line */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-sm">{r.name}</h3>
                      <HotScoreBadge score={r.hot_score} />
                      {r.official_star_rating && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/30 font-mono flex items-center gap-1">
                          <Award className="w-2.5 h-2.5" /> {r.official_star_rating}★ TGCSA
                        </span>
                      )}
                      <StarBar rating={r.rating} count={r.review_count} />
                      {r.price_level && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 font-mono">
                          {PRICE_LABEL[r.price_level] || r.price_level}
                        </span>
                      )}
                      {!r._enriched && quickScan && enrichProgress.total > 0 && enrichProgress.done < enrichProgress.total && (
                        <span className="text-[10px] text-[#647087] flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> enriching…</span>
                      )}
                    </div>

                    {/* Meta row */}
                    <div className="text-[11px] text-[#a0aabf] flex items-center gap-3 mt-1.5 flex-wrap">
                      {r.address && <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{r.address}</span>}
                      {r.phone && <span className="flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{r.phone}</span>}
                      {r.website && (
                        <a href={r.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-[#1fb6c1]">
                          <Globe className="w-2.5 h-2.5" /> website <ExternalLink className="w-2 h-2 opacity-50" />
                        </a>
                      )}
                    </div>

                    {/* Enrichment row */}
                    {r._enriched && (
                      <div className="text-[11px] text-[#d4dbe7] flex items-center gap-3 mt-2 flex-wrap pt-2 border-t border-white/5">
                        {priceRange && (
                          <span className="flex items-center gap-1 text-emerald-300 font-mono">
                            <DollarSign className="w-3 h-3" /> {priceRange}
                          </span>
                        )}
                        {r.rooms_count && (
                          <span className="flex items-center gap-1 text-[#A78BFA]">
                            <Building2 className="w-3 h-3" /> {r.rooms_count} {r.rooms_count === 1 ? "room" : "rooms/units"}
                          </span>
                        )}
                        {r.established_year && (
                          <span className="flex items-center gap-1 text-[#647087]">
                            <Calendar className="w-3 h-3" /> est. {r.established_year}
                          </span>
                        )}
                        {r.tgcsa_url && (
                          <a href={r.tgcsa_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-amber-200 hover:text-amber-100">
                            <Award className="w-3 h-3 inline" /> TGCSA listing
                          </a>
                        )}
                        {r.wikipedia_url && (
                          <a href={r.wikipedia_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#1fb6c1] hover:text-white">
                            Wikipedia
                          </a>
                        )}
                      </div>
                    )}

                    {/* Manual enrich button when Quick scan was off */}
                    {!quickScan && !r._enriched && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); enrichOne(realIdx, r); }}
                        className="text-[10px] text-amber-300 hover:text-amber-100 mt-2 flex items-center gap-1"
                        data-testid={`discover-enrich-${r.place_id}`}
                      >
                        <Zap className="w-3 h-3" /> Enrich now (TGCSA + pricing + Wikipedia)
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {!loading && results.length === 0 && !err && (
            <div className="p-10 text-center text-xs text-[#647087]">
              Enter a location above and click Search to find new prospects.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
